/**
 * The flag *definition* — the document `GET /sdk/v1/definitions` serves and
 * `PUT /admin/v1/environments/:env/flags/:flag/definition` accepts.
 *
 * `spec/SPEC.md` §4–§5 is the normative description; this file is its zod
 * transcription and the source of `schema.json`.
 */
import { z } from "zod";
import { CONTROL, changeNumber, flagKey, segmentName, treatmentName, } from "./common.js";
/** A semver-ish version string: 1–3 numeric parts, optional pre-release/build. */
export const SEMVER_PATTERN = /^\d+(\.\d+){0,2}(-[0-9A-Za-z.-]+)?(\+[0-9A-Za-z.-]+)?$/;
export const semverString = z
    .string()
    .regex(SEMVER_PATTERN, "must look like 1, 1.2, 1.2.3, 1.2.3-beta.1 or 1.2.3+build");
export const attributeName = z.string().min(1).max(100);
/** Scalar operand of a comparison matcher. */
const scalar = z.union([z.string(), z.number().finite(), z.boolean()]);
const withMatcher = (matcher, shape) => z.object({ matcher: z.literal(matcher), ...shape }).strict();
/** Segment matchers always apply to the context key (SPEC §5.2). */
const segmentShape = {
    attribute: z.literal("key").default("key"),
    value: segmentName,
};
const equalityShape = { attribute: attributeName, value: scalar };
const listShape = { attribute: attributeName, values: z.array(scalar).min(1).max(5000) };
const stringShape = { attribute: attributeName, value: z.string().min(1).max(500) };
const numberShape = { attribute: attributeName, value: z.number().finite() };
const numberRangeShape = {
    attribute: attributeName,
    min: z.number().finite(),
    max: z.number().finite(),
};
const semverShape = { attribute: attributeName, value: semverString };
const semverRangeShape = { attribute: attributeName, min: semverString, max: semverString };
const presenceShape = { attribute: attributeName };
const stringListShape = {
    attribute: attributeName,
    values: z.array(z.string()).min(1).max(5000),
};
/** Every matcher name, in the order SPEC §5 documents them. */
export const MATCHERS = [
    "in_segment",
    "not_in_segment",
    "equals",
    "not_equals",
    "in_list",
    "not_in_list",
    "starts_with",
    "ends_with",
    "contains",
    "gt",
    "gte",
    "lt",
    "lte",
    "between",
    "semver_eq",
    "semver_gte",
    "semver_lte",
    "semver_between",
    "is_set",
    "is_true",
    "is_false",
    "list_contains_any",
    "list_contains_all",
];
export const condition = z
    .discriminatedUnion("matcher", [
    withMatcher("in_segment", segmentShape),
    withMatcher("not_in_segment", segmentShape),
    withMatcher("equals", equalityShape),
    withMatcher("not_equals", equalityShape),
    withMatcher("in_list", listShape),
    withMatcher("not_in_list", listShape),
    withMatcher("starts_with", stringShape),
    withMatcher("ends_with", stringShape),
    withMatcher("contains", stringShape),
    withMatcher("gt", numberShape),
    withMatcher("gte", numberShape),
    withMatcher("lt", numberShape),
    withMatcher("lte", numberShape),
    withMatcher("between", numberRangeShape),
    withMatcher("semver_eq", semverShape),
    withMatcher("semver_gte", semverShape),
    withMatcher("semver_lte", semverShape),
    withMatcher("semver_between", semverRangeShape),
    withMatcher("is_set", presenceShape),
    withMatcher("is_true", presenceShape),
    withMatcher("is_false", presenceShape),
    withMatcher("list_contains_any", stringListShape),
    withMatcher("list_contains_all", stringListShape),
])
    .superRefine((value, ctx) => {
    if (value.matcher === "between" && value.min > value.max) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "min must be <= max", path: ["min"] });
    }
});
export const bucketPercent = z.number().int().min(0).max(100);
export const distributionEntry = z
    .object({ treatment: treatmentName, percent: bucketPercent })
    .strict();
/** Percents are integers summing to exactly 100 (SPEC §4.4). */
export const distribution = z
    .array(distributionEntry)
    .min(1)
    .max(50)
    .superRefine((entries, ctx) => {
    const total = entries.reduce((sum, e) => sum + e.percent, 0);
    if (total !== 100) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `distribution percents must sum to 100, got ${total}`,
        });
    }
    const seen = new Set();
    entries.forEach((entry, index) => {
        if (seen.has(entry.treatment)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `treatment "${entry.treatment}" appears twice in one distribution`,
                path: [index, "treatment"],
            });
        }
        seen.add(entry.treatment);
    });
});
export const ruleId = z.string().regex(/^[a-zA-Z0-9][a-zA-Z0-9_-]{0,63}$/);
export const rule = z
    .object({
    id: ruleId,
    label: z.string().max(200).optional(),
    conditions: z.array(condition).min(1).max(20),
    distribution,
})
    .strict();
/**
 * A treatment and its optional dynamic config. The config is an arbitrary JSON
 * value **serialized as a string** on the wire, exactly like Split — SDKs hand
 * the raw string back and the app parses it (SPEC §4.2).
 */
export const treatment = z
    .object({
    name: treatmentName,
    config: z
        .string()
        .max(32_768)
        .refine((raw) => {
        try {
            JSON.parse(raw);
            return true;
        }
        catch {
            return false;
        }
    }, { message: "config must be a JSON value serialized as a string" })
        .optional(),
})
    .strict();
/** A 32-bit signed seed as stored in the definition. */
export const seed = z.number().int().min(-2_147_483_648).max(2_147_483_647);
const flagDefinitionShape = {
    flag: flagKey,
    version: z.number().int().min(1),
    changeNumber,
    enabled: z.boolean(),
    killed: z.boolean(),
    treatments: z.array(treatment).min(1).max(50),
    defaultTreatment: treatmentName,
    trafficAllocation: z.number().int().min(0).max(100),
    trafficAllocationSeed: seed,
    seed,
    rules: z.array(rule).max(100),
    defaultRule: distribution,
};
function checkTreatmentReferences(value, ctx) {
    const names = new Set();
    value.treatments.forEach((t, index) => {
        if (t.name === CONTROL) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: '"control" is reserved and may not be declared as a treatment',
                path: ["treatments", index, "name"],
            });
        }
        if (names.has(t.name)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `duplicate treatment "${t.name}"`,
                path: ["treatments", index, "name"],
            });
        }
        names.add(t.name);
    });
    if (!names.has(value.defaultTreatment)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `defaultTreatment "${value.defaultTreatment}" is not a declared treatment`,
            path: ["defaultTreatment"],
        });
    }
    const ids = new Set();
    value.rules.forEach((r, ruleIndex) => {
        if (ids.has(r.id)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `duplicate rule id "${r.id}"`,
                path: ["rules", ruleIndex, "id"],
            });
        }
        ids.add(r.id);
        r.distribution.forEach((entry, entryIndex) => {
            if (!names.has(entry.treatment)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `treatment "${entry.treatment}" is not a declared treatment`,
                    path: ["rules", ruleIndex, "distribution", entryIndex, "treatment"],
                });
            }
        });
    });
    value.defaultRule.forEach((entry, entryIndex) => {
        if (!names.has(entry.treatment)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `treatment "${entry.treatment}" is not a declared treatment`,
                path: ["defaultRule", entryIndex, "treatment"],
            });
        }
    });
}
/** A complete flag definition as served to SDKs. */
export const flagDefinition = z
    .object(flagDefinitionShape)
    .strict()
    .superRefine(checkTreatmentReferences);
/**
 * The body of `PUT /admin/v1/environments/:env/flags/:flag/definition`: the
 * editable half of a definition plus the optimistic-concurrency guard. The
 * service owns `flag`, `version` and `changeNumber`.
 */
export const flagDefinitionPut = z
    .object({
    expectedVersion: z.number().int().min(0),
    enabled: z.boolean(),
    killed: z.boolean().optional(),
    treatments: flagDefinitionShape.treatments,
    defaultTreatment: flagDefinitionShape.defaultTreatment,
    trafficAllocation: flagDefinitionShape.trafficAllocation.default(100),
    trafficAllocationSeed: seed.optional(),
    seed: seed.optional(),
    rules: flagDefinitionShape.rules.default([]),
    defaultRule: flagDefinitionShape.defaultRule,
    comment: z.string().max(500).optional(),
})
    .strict()
    .superRefine(checkTreatmentReferences);
/** Why an evaluation returned the treatment it returned (SPEC §9). */
export const REASONS = [
    "not_found",
    "disabled",
    "killed",
    "not_in_traffic_allocation",
    "rule",
    "default_rule",
];
export const evaluationReason = z.enum(REASONS);
export const evaluationResult = z
    .object({
    treatment: z.string(),
    reason: evaluationReason,
    ruleId: z.string().optional(),
    config: z.string().optional(),
    changeNumber: changeNumber.optional(),
})
    .strict();
//# sourceMappingURL=definition.js.map